import { useState, useEffect, useCallback, useRef } from 'react'
import { pick } from 'lodash'
import { ChallengeService } from '@duck/api-definitions'
import { apis } from '@modules/apiClients'
import { useAuth } from '@/hooks/useAuth'
import { useDesktopEffect } from '@/hooks/useDesktopEffect'
import {
    isSavingWorkAtom,
    lastSaveWorkDateAtom,
    workTimeAtom,
    currentSolutionAtom,
    currentQueryAtom,
} from '@/store/saveWorkAtoms'
import { useAtom, useSetAtom } from 'jotai'
import { useWorkTimeReporting } from '@/hooks/useWorkTimeReporting'
import { useTar } from '@/modules/i18n'
import { debounce } from 'lodash'
import { useLocalSettings } from '@/hooks/useLocalSettings'
import { Workspace } from './Workspace'
import { WorkspaceError, challengeErrorsToNotify } from './WorkspaceError'
import { WorkspaceInitialization } from './WorkspaceInitialization'
import { getChallengeErrorCode } from '@/utils/errorUtils'
import { toast } from 'sonner'
import * as Sentry from '@sentry/react'
import { useMemo } from 'react'
import { useDebouncedEffect } from '@/hooks/useDebouncedEffect'
import { queryClient } from '@/modules/queryClient'
import { ongoingChallengesQueryKeys } from '@/hooks/useOngoingChallenges'
import {
    Dialog,
    DialogContent,
    Button,
    MarkdownEditor,
    useIsMobile,
} from '@duck/ui'
import { driver } from 'driver.js'
import type { DriveStep } from 'driver.js'
import 'driver.js/dist/driver.css'
import { getWorkspaceTourSteps } from './workspace-tour'

interface WorkspaceAllParentProps {
    slug: string
}

export const WorkspaceParent = ({
    slug,
}: WorkspaceAllParentProps) => {
    const { isAuthenticated } = useAuth()
    const [challengeId, setChallengeId] = useState<string | null>(null)
    const [challenge, setChallenge] = useState<
        ChallengeService.ChallengeInfo['challenge'] | null
    >(null)
    const [task, setTask] = useState<ChallengeService.ChallengeInfo['task'] | null>(
        null
    )
    const [charts, setCharts] = useState<
        ChallengeService.WorkState['charts'] | null
    >(null)
    const [error, setError] = useState<ChallengeService.ChallengeErrorCode | null>(null)
    const [isVisible, setIsVisible] = useState(false)
    const [isDisclaimerDialogOpen, setIsDisclaimerDialogOpen] = useState(false)
    const { getSetting, setSetting } = useLocalSettings()
    const isMobile = useIsMobile()
    const hasInitializedTourRef = useRef(false)
    const tourRef = useRef<ReturnType<typeof driver> | null>(null)

    const setIsSaving = useSetAtom(isSavingWorkAtom)
    const setLastSaveDate = useSetAtom(lastSaveWorkDateAtom)
    const [workTime, setWorkTime] = useAtom(workTimeAtom)
    const setCurrentSolution = useSetAtom(currentSolutionAtom)
    const setCurrentQuery = useSetAtom(currentQueryAtom)
    const { t } = useTar()

    const isBooting = useMemo(() => {
        const storedSlug = sessionStorage.getItem('challenge_unbooted')
        return Boolean(storedSlug && storedSlug === slug)
    }, [slug])

    useWorkTimeReporting(challengeId)

    useDebouncedEffect(() => {
        const fetchChallenge = async () => {
            try {
                setError(null)
                const [{ data: { challenge, task } }] = await Promise.all([
                    apis.challengeService.getChallengeBySlug(slug),
                    new Promise(res => setTimeout(res, isBooting ? 6500 : 400)),
                ])
                sessionStorage.removeItem('challenge_unbooted')
                setChallenge(challenge)
                setChallengeId(challenge.id)
                setWorkTime(challenge.workTime)
                setTask(task)
                setCharts(challenge.workState?.charts || {})
                const solution = challenge.workState?.solution || ''
                setCurrentSolution(solution)
                const query = challenge.workState?.lastQuery || task.defaultQuery || ''
                setCurrentQuery(query)
                if (isBooting) {
                    await queryClient.invalidateQueries({ queryKey: ongoingChallengesQueryKeys.all() })
                }
            } catch (err) {
                const errorCode = getChallengeErrorCode(err) as ChallengeService.ChallengeErrorCode
                if (challengeErrorsToNotify.includes(errorCode)) {
                    Sentry.captureException(err) // Extra report from client on criticals
                }
                setError(errorCode || 'unknownError')
            }
        }

        if (slug) {
            if (isAuthenticated) {
                fetchChallenge()
            } else {
                setError('loggedOut')
            }
        }
    }, [slug, isAuthenticated])

    useDesktopEffect(() => {
        // Prevent scrolling
        document.body.style.overflow = 'hidden'

        // Cleanup function to restore scrolling
        return () => {
            document.body.style.overflow = ''
        }
    }, [])

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    // Handler to close disclaimer dialog and mark as dismissed
    const handleCloseDisclaimer = useCallback(() => {
        setIsDisclaimerDialogOpen(false)
        if (challengeId) {
            const dismissedDisclaimers = getSetting('challengeDisclaimerDismissed') || {}
            setSetting('challengeDisclaimerDismissed', {
                ...dismissedDisclaimers,
                [challengeId]: true,
            })
        }
    }, [challengeId, getSetting, setSetting])

    // Fade in Workspace when all data is ready
    useEffect(() => {
        if (challenge && challengeId && task && charts && !error) {
            // Small delay to ensure smooth transition
            const timer = setTimeout(() => {
                setIsVisible(true)
            }, 50)
            return () => clearTimeout(timer)
        } else {
            setIsVisible(false)
        }
    }, [challenge, challengeId, task, charts, error])

    // Tour steps memoization
    const tourSteps = useMemo<DriveStep[]>(
        () => {
            if (!challenge) return []
            return getWorkspaceTourSteps(!!challenge.assignmentId).map(
                ({ title, description, popover, ...rest }) => ({
                    ...rest,
                    popover: {
                        ...(popover ?? {}),
                        title: t(title),
                        description: t(description),
                    },
                })
            )
        },
        [t, challenge?.assignmentId]
    )

    // Tour initialization
    useEffect(() => {
        if (challenge?.challengeDisclaimer && challengeId) {
            const dismissedDisclaimers = getSetting('challengeDisclaimerDismissed') || {}
            const isDismissed = dismissedDisclaimers[challengeId] === true

            if (!isDismissed) {
                setIsDisclaimerDialogOpen(true)
                return
            }
        }
        if (
            !isVisible ||
            !challenge ||
            hasInitializedTourRef.current ||
            isMobile ||
            getSetting('didTour') === true
        ) {
            return
        }

        hasInitializedTourRef.current = true
        tourRef.current = driver({
            showProgress: true,
            allowClose: false,
            steps: tourSteps,
            nextBtnText: t('common.next'),
            prevBtnText: t('common.previous'),
            doneBtnText: t('common.ok'),
            onDestroyed: () => {
                hasInitializedTourRef.current = false
                setSetting('didTour', true)
            },
        })
        tourRef.current.drive()

        return () => {
            tourRef.current?.destroy()
            tourRef.current = null
            hasInitializedTourRef.current = false
        }
    }, [isVisible, challenge, challengeId, isMobile, tourSteps, t, getSetting, setSetting])

    const handleChartAdded = useCallback(
        (solution: string, chart: ChallengeService.Chart) => {
            if (!challengeId) return
            setCurrentSolution(solution)
            setCharts((prev) => {
                if (!chart.id) {
                    return prev
                }
                const newCharts = { ...prev, [chart.id]: chart }
                setIsSaving(true)
                apis.challengeService
                    .saveWork({
                        challengeId,
                        workState: {
                            solution,
                            charts: newCharts,
                        },
                    })
                    .then(() => {
                        setLastSaveDate(new Date())
                    })
                    .catch((err) => {
                        console.error('Failed to save chart:', err)
                        toast.error(t('common.failedToSaveChart'))
                    })
                    .finally(() => {
                        setIsSaving(false)
                    })

                return newCharts
            })
        },
        [challengeId]
    )

    const handleChartRemoved = useCallback(
        (solution: string, remainingChartIds: string[]) => {
            if (!challengeId) {
                return
            }
            setCurrentSolution(solution)
            setCharts((prev) => {
                const charts = pick(
                    prev,
                    remainingChartIds
                ) as ChallengeService.WorkState['charts']
                setIsSaving(true)
                apis.challengeService
                    .saveWork({
                        challengeId,
                        workState: {
                            solution,
                            charts,
                        },
                    })
                    .then(() => {
                        setLastSaveDate(new Date())
                    })
                    .catch((err) => {
                        console.error('Failed to save chart:', err)
                        toast.error(t('common.failedToSaveChart'))
                    })
                    .finally(() => {
                        setIsSaving(false)
                    })
                return charts
            })
        },
        [challengeId]
    )

    const handleSolutionChange = useCallback(
        debounce(async (solution: string) => {
            if (!challengeId) {
                return
            }
            setIsSaving(true)
            try {
                await apis.challengeService.saveWork({
                    challengeId,
                    workState: {
                        solution,
                    },
                })
                setLastSaveDate(new Date())
            } catch (err) {
                console.error('Failed to save solution:', err)
            } finally {
                setIsSaving(false)
            }
        }, 1000),
        [challengeId]
    )

    if (error) {
        return <WorkspaceError error={error} />
    }

    if (
        !challenge || !challengeId ||
        !task ||
        !charts
    ) {
        return <WorkspaceInitialization isBooting={isBooting} />
    }

    return (
        <>
            <div
                className="flex flex-1"
                data-id={`workspace-${challengeId}`}
                style={{
                    opacity: isVisible ? 1 : 0,
                    transition: 'opacity 0.2s ease-in-out',
                }}
            >
                <Workspace
                    challenge={challenge}
                    task={task}
                    charts={charts}
                    challengeId={challengeId}
                    workTime={workTime}
                    handleSolutionChange={handleSolutionChange}
                    handleChartAdded={handleChartAdded}
                    handleChartRemoved={handleChartRemoved}
                />
            </div>

            {challenge?.challengeDisclaimer && (
                <Dialog
                    open={isDisclaimerDialogOpen}
                    onOpenChange={(open) => {
                        if (!open) {
                            handleCloseDisclaimer()
                        } else {
                            setIsDisclaimerDialogOpen(open)
                        }
                    }}
                >
                    <DialogContent width="lg" className="max-h-[90vh] flex flex-col">
                        <div className="flex-1 overflow-hidden min-h-[400px]">
                            <MarkdownEditor
                                defaultValue={challenge.challengeDisclaimer}
                                mode="view"
                                className="h-full"
                                noBorder
                            />
                        </div>
                        <div className="flex justify-end mt-4">
                            <Button onClick={handleCloseDisclaimer}>
                                {t('common.close')}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </>
    )
}

